import { normalizeOperationOutcome } from '@medplum/core'
import type { Resource, ResourceType } from '@medplum/fhirtypes'

// Thrown when a PUT-shaped mutation (`useFhirSaveMutation` /
// `useFhirUpsertMutation`) fails its `If-Match` precondition, indicating
// the target resource was modified between our read and write.
//
// Carries the latest server version so the caller's `onVersionConflict`
// handler can rehydrate form state without losing the user's in-progress
// edits (e.g., via `form.reset(latest, { keepDirtyValues: true })`).
//
// Field name `fhirResourceType` (not `resourceType`) avoids the
// `seen/no-resource-type-in-classes` rule and prevents this error from
// being mistaken for a FHIR resource by Medplum's `isResource` check.
export class ResourceVersionConflictError extends Error {
  readonly fhirResourceType: ResourceType
  readonly resourceId: string
  readonly fromVersionId: string | undefined
  readonly latest: Resource

  constructor(args: {
    fhirResourceType: ResourceType
    resourceId: string
    fromVersionId: string | undefined
    latest: Resource
    cause?: unknown
  }) {
    super(
      `${args.fhirResourceType}/${args.resourceId} was modified by another user. ` +
        `Please review the latest version and try again.`
    )
    this.name = 'ResourceVersionConflictError'
    this.fhirResourceType = args.fhirResourceType
    this.resourceId = args.resourceId
    this.fromVersionId = args.fromVersionId
    this.latest = args.latest
    if (args.cause !== undefined) {
      this.cause = args.cause
    }
  }
}

// Heuristic check for HTTP 412 Precondition Failed errors returned by FHIR
// servers when an `If-Match` header doesn't match the current versionId.
// Medplum surfaces these as OperationOutcomes; issue codes / messages vary
// slightly between server versions, so we check several signals.
export function isVersionConflictError(cause: unknown): boolean {
  const outcome = normalizeOperationOutcome(cause)
  return !!outcome.issue?.some((issue) => {
    if (issue.code === 'conflict') {
      return true
    }
    const text = (issue.details?.text ?? issue.diagnostics ?? '').toLowerCase()
    return text.includes('precondition failed') || text.includes('if-match')
  })
}

export function ifMatchHeader(
  versionId: string | undefined
): Record<string, string> | undefined {
  if (!versionId) {
    return undefined
  }
  return { 'If-Match': `W/"${versionId}"` }
}
